import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoChevronLeft as LeftIcon, GoChevronRight as RightIcon } from 'react-icons/go';
import useSearchRules from 'hooks/useSearchRules';
import s from './Pagination.module.css';

interface IPaginationProps {
  totalPages: number,
}

const Pagination = ({ totalPages }: IPaginationProps) => {
  const { getRules } = useSearchRules();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialPage = +(searchParams.get('page') || 1);

  const [currentPage, setCurrentPage] = useState<number>(initialPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [totalPages]);

  useEffect(() => {
    const move = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return;
      }

      let pageNumber;

      if (event.key === 'ArrowLeft') {
        pageNumber = currentPage === totalPages ? totalPages : +currentPage + 1;
      } else {
        pageNumber = currentPage === 1 ? 1 : +currentPage - 1;
      }

      setCurrentPage(pageNumber);
      getRules([
        { page: pageNumber },
      ]);
    };

    document.body.addEventListener('keydown', move);

    return () => document.body.removeEventListener('keydown', move);
  }, [currentPage, getRules, totalPages]);

  useEffect(() => {
    searchParams.set('page', `${currentPage}`);
    setSearchParams(searchParams);
  }, [currentPage, setSearchParams, searchParams]);

  if (totalPages <= 1) {
    return null;
  }

  const handlePrevClick = () => {
    setCurrentPage((prev) => (prev === 1 ? 1 : +prev - 1));
    getRules([
      { page: currentPage - 1 },
    ]);
  };

  const handleNextClick = () => {
    setCurrentPage((prev) => (prev === totalPages ? totalPages : +prev + 1));
    getRules([
      { page: currentPage + 1 },
    ]);
  };

  const handlePageNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pageNumber = +e.target.value;

    if (pageNumber < 1) {
      setCurrentPage(1);
    } else if (pageNumber > totalPages) {
      setCurrentPage(totalPages);
    } else {
      setCurrentPage(pageNumber);
    }
  };

  const handlePageNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const pageNumber = +e.target.value;

    getRules([
      { page: pageNumber },
    ]);
  };

  return (
    <div className={s.container}>
      <button
        className={s.prev}
        type="button"
        onClick={handlePrevClick}
        disabled={currentPage === 1}
      >
        <LeftIcon className={s.icon} />
      </button>
      <input
        type="number"
        className={s.currentPage}
        value={currentPage}
        onChange={handlePageNumberChange}
        onBlur={handlePageNumberBlur}
        min={1}
        max={totalPages}
      />
      <span className={s.totalPages}>{totalPages}</span>
      <button
        className={s.next}
        type="button"
        disabled={currentPage === totalPages}
        onClick={handleNextClick}
      >
        <RightIcon />
      </button>
    </div>
  );
};

export default Pagination;
